/**
 * Pearl shell icon stack configuration — Figma rebrand.
 *
 * The nav stack is now a hierarchical tree, not a flat list. Two of the six
 * top-level entries are *groups* that pop a hover-dropdown to the left of the
 * pill (Practice, Insights). The other four are leaf entries that route to a
 * single Pearl product page (Second Opinion, Treatment, Insurance, Voice).
 *
 * Order matches the visual top-to-bottom layout of `pearl-widget.pen`. Voice
 * is intentionally placed last (closest to the FAB) so it is the first icon
 * the user's thumb lands on when the stack opens.
 *
 * Back-compat exports `ICONS`, `PEARL_ACTIONS`, and `NON_VOICE_ACTIONS` are
 * preserved as a *flattened* leaves list so the popup controller, sandbox
 * mapper, and existing tests keep working without rewrites.
 */

/** Every URL-bearing leaf in the tree has one of these action ids. */
export type LeafAction =
  // Top-level leaves
  | 'tooth'
  | 'treatments'
  | 'insurance'
  | 'voice'
  | 'daily-huddle'
  | 'patient-report'
  | 'perio'
  // Practice group sub-items
  | 'calendar'
  | 'patient'
  | 'canceled'
  | 'call-sheets'
  // Insights group sub-items
  | 'dashboard'
  | 'production'
  | 'funnels';

/** Hover-dropdown groups (no URL of their own — only their children open URLs). */
export type GroupId = 'practice' | 'insights';

/**
 * Back-compat alias — the URL builder, sandbox mapper, and a number of
 * popup-side helpers still type their `action` parameter as `PearlAction`.
 * It now resolves to the leaf action union.
 */
export type PearlAction = LeafAction;

export interface NavLeaf {
  kind: 'leaf';
  action: LeafAction;
  label: string;
  /**
   * Path under `icons/widget/` (no `.svg` suffix). Sub-item icons live under
   * `dropdown/`, e.g. `dropdown/schedule`.
   */
  iconFile: string;
  /**
   * If true, the leaf is rendered into the DOM but hidden via CSS
   * (`display: none`). Use this to pull an icon out of the visible stack
   * while keeping the action wiring (handlers, URL routes, tests) intact —
   * a soft "comment out" that doesn't churn the rest of the codebase.
   */
  hidden?: boolean;
  /**
   * If true, renders a subtle 15×1px horizontal divider line above this leaf
   * in the vertical icon stack (DOCK-128).
   */
  dividerBefore?: boolean;
  /**
   * Side-panel Dock copy (DOCK-174) when it differs from the on-page tooltip,
   * e.g. "Recently Cancelled". Omit to reuse {@link label}.
   */
  panelLabel?: string;
  /** Side-panel Dock menu copy, when it differs from `panelLabel`. */
  dockLabel?: string;
  /** Side-panel Dock: disabled until the PMS tab has a patient open. */
  requiresPatient?: boolean;
}

export interface NavGroup {
  kind: 'group';
  id: GroupId;
  label: string;
  /** The big top-level icon shown in the rest stack — same path convention as leaf. */
  iconFile: string;
  children: readonly NavLeaf[];
}

export type NavNode = NavLeaf | NavGroup;

export const VOICE_ACTION: LeafAction = 'voice';
export const PERIO_ACTION: LeafAction = 'perio';

/**
 * Top-down nav tree as it renders in the Figma design. The flat `ICONS` array
 * below is derived from this so there's exactly one source of truth.
 */
export const NAV_TREE: readonly NavNode[] = [
  {
    kind: 'leaf',
    action: 'tooth',
    label: 'Second Opinion',
    iconFile: 'tooth',
  },
  {
    kind: 'group',
    id: 'practice',
    label: 'Practice',
    iconFile: 'practice',
    children: [
      {
        kind: 'leaf',
        action: 'calendar',
        label: 'Schedule',
        iconFile: 'dropdown/schedule',
      },
      {
        kind: 'leaf',
        action: 'patient',
        label: 'Patients',
        iconFile: 'dropdown/patients',
      },
      {
        kind: 'leaf',
        action: 'canceled',
        label: 'Canceled',
        iconFile: 'dropdown/canceled',
        panelLabel: 'Recently Cancelled',
      },
      {
        kind: 'leaf',
        action: 'call-sheets',
        label: 'Call sheets',
        iconFile: 'dropdown/call-sheets',
        panelLabel: 'Call Sheet',
      },
    ],
  },
  {
    kind: 'group',
    id: 'insights',
    label: 'Insights',
    iconFile: 'insights',
    children: [
      {
        kind: 'leaf',
        action: 'dashboard',
        label: 'Dashboard',
        iconFile: 'dropdown/dashboard',
      },
      {
        kind: 'leaf',
        action: 'production',
        label: 'Production',
        iconFile: 'dropdown/production',
      },
      {
        kind: 'leaf',
        action: 'funnels',
        label: 'Funnels',
        iconFile: 'dropdown/funnels',
      },
    ],
  },
  {
    kind: 'leaf',
    action: 'treatments',
    label: 'Treatment',
    iconFile: 'treatments',
    panelLabel: 'Treatments',
    dockLabel: 'Treatment',
  },
  {
    kind: 'leaf',
    action: 'insurance',
    label: 'Insurance',
    iconFile: 'insurance',
  },
  {
    // Voice is currently hidden from the visible icon stack — the leaf is
    // still rendered into the DOM (so click handlers, tests, and the iframe
    // wiring keep working) but `hidden: true` paints `display: none`. To
    // re-introduce the icon visually, just delete this `hidden` flag.
    kind: 'leaf',
    action: 'voice',
    label: 'Voice',
    iconFile: 'voice',
    hidden: true,
  },
  {
    kind: 'leaf',
    action: 'daily-huddle',
    label: 'Daily Huddle',
    iconFile: 'daily-huddle',
    dividerBefore: true,
  },
  {
    kind: 'leaf',
    action: 'patient-report',
    label: 'Patient Report',
    iconFile: 'patient-report',
    panelLabel: 'Patient Printout',
    requiresPatient: true,
  },
  {
    kind: 'leaf',
    action: 'perio',
    label: 'Perio',
    iconFile: 'perio',
    hidden: true,
  },
];

/**
 * Walks the nav tree depth-first and returns every leaf in document order.
 * Used by `ICONS` (back-compat) and by callers that want to enumerate every
 * URL-bearing action (e.g. tests, sandbox mapper).
 *
 * @param tree The nav tree to flatten.
 * @returns A flat ordered list of every `NavLeaf`.
 *
 * @example
 *   flattenLeaves(NAV_TREE).map(l => l.action);
 *   // → ['tooth','calendar','patient','canceled','call-sheets',
 *   //    'dashboard','production','funnels','treatments','insurance','voice']
 */
export function flattenLeaves(tree: readonly NavNode[]): readonly NavLeaf[] {
  const out: NavLeaf[] = [];
  for (const node of tree) {
    if (node.kind === 'leaf') {
      out.push(node);
    } else {
      for (const child of node.children) out.push(child);
    }
  }
  return out;
}

/**
 * Back-compat shape kept for popup/sandbox-mapper callers. Same fields as
 * `NavLeaf` minus the `kind` discriminant.
 */
export interface PearlIcon {
  action: LeafAction;
  label: string;
  iconFile: string;
}

/**
 * Flattened view of every leaf in `NAV_TREE` (12 entries: 5 top-level + 4
 * Practice + 3 Insights). Used by callers that don't care about the dropdown
 * hierarchy (e.g. enumerating every action that has a URL).
 */
export const ICONS: readonly PearlIcon[] = flattenLeaves(NAV_TREE).map(l => ({
  action: l.action,
  label: l.label,
  iconFile: l.iconFile,
}));

export const PEARL_ACTIONS: readonly LeafAction[] = ICONS.map(i => i.action);
export const NON_VOICE_ACTIONS: readonly LeafAction[] = PEARL_ACTIONS.filter(
  a => a !== VOICE_ACTION && a !== PERIO_ACTION,
);
